import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { routePaths } from 'router';
import type { TWarehouseTaskCreateReq } from 'api/swagger';
import { useCreateWarehouseTask } from 'api/warehouse-task/use-create-warehouse-task';
import { withRoleGuard } from 'shared/hocs/with-role-guard';
import { UserRole } from 'shared/hooks/use-role-guard';
import { formatWhTaskDates } from 'shared/utils/wh-tasks/format-wh-task-dates';
import { WHTaskType } from 'types/wh-task';
import { EFormDrawerSize, FormDrawerWithButton } from 'components/common/form-drawer-with-button';
import { WhTaskForm } from 'components/wh-tasks/wh-task-form';
import type { IWhTaskFormFields } from 'components/wh-tasks/wh-task-form/types';

export const WhTaskCreateComponent: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { mutateAsync: createWarehouseTask, isLoading } = useCreateWarehouseTask();

  const onFinish = async (values: IWhTaskFormFields) => {
    const { arrivalDate, departureDate } = formatWhTaskDates(values.date);

    const apiVariables: TWarehouseTaskCreateReq = {
      type: values.type,
      clientId: values.client.value,
      contactId: values.contact.value,
      warehouseId: values.warehouseId,
      totalWeight: values.totalWeight,
      additionalInfo: values.additionalInfo,
      currency: values.currency,
      freight: values.freight,
      clientOrderNumber: values.clientOrderNumber,
      warehouseTaskItems: values.warehouseTaskItems,
      arrivalDate,
      departureDate,
      ...(values.type === WHTaskType.reloading && {
        plateNumberFrom: values.plateNumberFrom,
        plateNumberTo: values.plateNumberTo,
      }),
    };
    const result = await createWarehouseTask(apiVariables);

    navigate(generatePath(routePaths.whTask, { whTaskId: String(result.id) }));
  };

  return (
    <FormDrawerWithButton
      formComponent={WhTaskForm}
      title={t('wh-task:wh_task_create')}
      buttonLabel={t('wh-task:wh_task_add')}
      isLoading={isLoading}
      size={EFormDrawerSize.LARGE}
      displayMaskClickConfirmation
      onFinish={onFinish}
    />
  );
};

export const WhTaskCreate = withRoleGuard(WhTaskCreateComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.customer_manager,
]);
